import { AppObject, OcctImportMesh } from '../types'

/**
 * Cut a part in two at a horizontal plane.
 *
 * Drinkware is routinely finished in two colours — a copper upper and a matte lower,
 * say — but arrives as a single mesh, so there is nothing to select and no way to give
 * the two bands different materials. Painting the split into a texture does not work
 * either: a pattern is flattened into the base colour with metalness forced off (see
 * patternTexture.ts), so a copper band would come out as brown paint rather than metal.
 * Splitting the geometry gives two genuine parts, each free to be metal or matte, and
 * each named in the exported GLB.
 *
 * Triangles straddling the plane are cut rather than assigned whole. The alternative —
 * giving each triangle to whichever side holds most of its corners — leaves a zigzag as
 * tall as one row of the mesh, still a visible ragged line where the colours meet.
 */

interface Side {
  position: number[]
  normal: number[]
  uv: number[]
  index: number[]
  /** Original vertex -> index in this side, so a shared vertex is emitted once. */
  fromVertex: Map<number, number>
  /** Cut point on an original edge -> index in this side. */
  fromEdge: Map<string, number>
  srcPosition: number[]
  srcNormal: number[]
  srcUv: number[]
}

function emptySide(): Side {
  return {
    position: [],
    normal: [],
    uv: [],
    index: [],
    fromVertex: new Map(),
    fromEdge: new Map(),
    srcPosition: [],
    srcNormal: [],
    srcUv: [],
  }
}

/**
 * Split `object` at `height`, measured in the part's own geometry space.
 * Returns [above, below], or null when the plane misses the part.
 */
export function splitObjectByHeight(object: AppObject, height: number): [AppObject, AppObject] | null {
  const pos = object.mesh.attributes.position?.array
  const norm = object.mesh.attributes.normal?.array
  const uv = object.mesh.attributes.uv?.array
  const idx = object.mesh.index?.array
  if (!pos || !norm || !idx) return null

  const src = object.sourceAttributes
  const hasUv = !!uv
  const srcHasNormal = !!src?.normal
  const srcHasUv = !!src?.uv

  const above = emptySide()
  const below = emptySide()

  const isAbove = (v: number) => pos[v * 3 + 1] >= height

  // Copy an original vertex into a side, once.
  const takeVertex = (side: Side, v: number): number => {
    const existing = side.fromVertex.get(v)
    if (existing !== undefined) return existing
    const at = side.position.length / 3
    side.position.push(pos[v * 3], pos[v * 3 + 1], pos[v * 3 + 2])
    side.normal.push(norm[v * 3], norm[v * 3 + 1], norm[v * 3 + 2])
    if (hasUv && uv) side.uv.push(uv[v * 2], uv[v * 2 + 1])
    if (src) {
      side.srcPosition.push(src.position[v * 3], src.position[v * 3 + 1], src.position[v * 3 + 2])
      if (srcHasNormal && src.normal) {
        side.srcNormal.push(src.normal[v * 3], src.normal[v * 3 + 1], src.normal[v * 3 + 2])
      }
      if (srcHasUv && src.uv) side.srcUv.push(src.uv[v * 2], src.uv[v * 2 + 1])
    }
    side.fromVertex.set(v, at)
    return at
  }

  // Where edge a-b crosses the plane, computed once per edge per side. The export
  // geometry is interpolated with the same weight: the loader only ever applies an
  // affine transform, so a point at t along the edge is at t in both frames.
  const takeCut = (side: Side, a: number, b: number): number => {
    const key = a < b ? `${a}_${b}` : `${b}_${a}`
    const existing = side.fromEdge.get(key)
    if (existing !== undefined) return existing

    const ya = pos[a * 3 + 1]
    const yb = pos[b * 3 + 1]
    const span = yb - ya
    const t = Math.abs(span) < 1e-12 ? 0.5 : (height - ya) / span
    const lerp = (arr: ArrayLike<number>, stride: number, comp: number) =>
      arr[a * stride + comp] + (arr[b * stride + comp] - arr[a * stride + comp]) * t

    const at = side.position.length / 3
    side.position.push(lerp(pos, 3, 0), height, lerp(pos, 3, 2))

    let nx = lerp(norm, 3, 0)
    let ny = lerp(norm, 3, 1)
    let nz = lerp(norm, 3, 2)
    const len = Math.hypot(nx, ny, nz) || 1
    nx /= len
    ny /= len
    nz /= len
    side.normal.push(nx, ny, nz)

    if (hasUv && uv) side.uv.push(lerp(uv, 2, 0), lerp(uv, 2, 1))

    if (src) {
      side.srcPosition.push(lerp(src.position, 3, 0), lerp(src.position, 3, 1), lerp(src.position, 3, 2))
      if (srcHasNormal && src.normal) {
        let sx = lerp(src.normal, 3, 0)
        let sy = lerp(src.normal, 3, 1)
        let sz = lerp(src.normal, 3, 2)
        const slen = Math.hypot(sx, sy, sz) || 1
        sx /= slen
        sy /= slen
        sz /= slen
        side.srcNormal.push(sx, sy, sz)
      }
      if (srcHasUv && src.uv) side.srcUv.push(lerp(src.uv, 2, 0), lerp(src.uv, 2, 1))
    }

    side.fromEdge.set(key, at)
    return at
  }

  for (let t = 0; t < idx.length; t += 3) {
    const tri = [idx[t], idx[t + 1], idx[t + 2]]
    const flags = [isAbove(tri[0]), isAbove(tri[1]), isAbove(tri[2])]
    const count = (flags[0] ? 1 : 0) + (flags[1] ? 1 : 0) + (flags[2] ? 1 : 0)

    if (count === 3 || count === 0) {
      const side = count === 3 ? above : below
      side.index.push(takeVertex(side, tri[0]), takeVertex(side, tri[1]), takeVertex(side, tri[2]))
      continue
    }

    // Rotate so the lone corner comes first. Cyclic order — and so the winding — holds.
    const lone = count === 1 ? flags.indexOf(true) : flags.indexOf(false)
    const a = tri[lone]
    const b = tri[(lone + 1) % 3]
    const c = tri[(lone + 2) % 3]
    const loneSide = count === 1 ? above : below
    const pairSide = count === 1 ? below : above

    // Edges a-b and c-a cross the plane; b-c does not.
    const pLone = takeCut(loneSide, a, b)
    const qLone = takeCut(loneSide, c, a)
    loneSide.index.push(takeVertex(loneSide, a), pLone, qLone)

    const pPair = takeCut(pairSide, a, b)
    const qPair = takeCut(pairSide, c, a)
    const bPair = takeVertex(pairSide, b)
    const cPair = takeVertex(pairSide, c)
    pairSide.index.push(pPair, bPair, cPair)
    pairSide.index.push(pPair, cPair, qPair)
  }

  if (above.index.length === 0 || below.index.length === 0) return null

  const build = (side: Side, id: string): AppObject => {
    const mesh: OcctImportMesh = {
      id,
      color: object.mesh.color,
      attributes: {
        position: { array: new Float32Array(side.position) },
        normal: { array: new Float32Array(side.normal) },
        ...(hasUv ? { uv: { array: new Float32Array(side.uv) } } : {}),
      },
      index: { array: new Uint32Array(side.index) },
    }
    return {
      id,
      color: object.color,
      mesh,
      material: object.material ? { ...object.material } : undefined,
      offset: object.offset ? [...object.offset] : undefined,
      hidden: object.hidden,
      logoV: object.logoV,
      sourceTransform: object.sourceTransform ? [...object.sourceTransform] : undefined,
      // Shared by reference: the halves are still the same authored surface, and its maps
      // are addressed by UVs that both halves inherit. (Tangents are not carried across a
      // split — three derives a frame per pixel when they are absent.)
      sourceMaterial: object.sourceMaterial,
      sourceAttributes: src
        ? {
            position: new Float32Array(side.srcPosition),
            normal: srcHasNormal ? new Float32Array(side.srcNormal) : undefined,
            uv: srcHasUv ? new Float32Array(side.srcUv) : undefined,
            index: new Uint32Array(side.index),
          }
        : undefined,
    }
  }

  return [build(above, `${object.id}_upper`), build(below, `${object.id}_lower`)]
}

/** Vertical extent of a part's geometry, for bounding the cut-height control. */
export function heightRangeOf(object: AppObject): { min: number; max: number } | null {
  const pos = object.mesh.attributes.position?.array
  if (!pos || pos.length < 3) return null
  let min = Infinity
  let max = -Infinity
  for (let i = 1; i < pos.length; i += 3) {
    if (pos[i] < min) min = pos[i]
    if (pos[i] > max) max = pos[i]
  }
  return { min, max }
}
